import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Tooltip,
} from "@material-ui/core";
import Skeleton from "react-loading-skeleton";
import Close from "@material-ui/icons/Close";
import Button from "components/Forms/CustomButtons/ButtonTS";
import React from "react";
import { ActivityItemWorker } from "services/input-core/work-items/@types";

import { useTranslation } from "react-i18next";
import { useGetPaginatedLogSystem } from "services/input-core/log-system/get-paginated-logs";
import {
  ISubjectStatusLogSystem,
  SubjectStatusLogSystemEnum,
} from "utils/@types";
import { Info } from "@material-ui/icons";
import { Box } from "@mui/material";
import { format } from 'date-fns';

import PaginatedTable from "components/Tables/ReactTable/ManualPaginatedTable";

import { ColumnDef } from "@tanstack/table-core";
import { useActivityItemContext } from "store/activityItem";

interface LogSystemModalProps {
  modalOpen: boolean;
  onClose: () => void;
}

const LogSystemModal: React.FC<LogSystemModalProps> = ({
  modalOpen,
  onClose
}) => {
  const { t } = useTranslation([
    "modals",
    "log-system",
    "log-description",
    "list-errors",
    "list-messages",
  ]);

  const { currentActivityItem } = useActivityItemContext(state => ({
    currentActivityItem: state.currentActivity,
  }));

  const [tableData, setTableData] = React.useState({
    pageSize: 10,
    pageIndex: 0,
    columnFilters: [],
    sorting: [],
  });

  const paramsGetLogSystem: any = {
    limit: tableData.pageSize,
    page: tableData.pageIndex + 1,
  };

  const {
    data: logSystemData,
    isLoading: isLogSystemData,
  } = useGetPaginatedLogSystem({
    zID: currentActivityItem?.zIdentifier,
    params: paramsGetLogSystem,
  });

  const getDescription = (subject: {
    key: ISubjectStatusLogSystem;
    value: string;
    translatableProperties?: {
      value: string;
      rawProperties: { [x: string]: string };
    };
  }) => {
    if (!subject.translatableProperties) {
      return subject.value ? t(subject.value, { ns: "log-description" }) : "";
    }

    if (subject?.value) return t(subject.value, { ns: "log-description" });

    const description = t(subject.translatableProperties.value, {
      ns: "log-description",
    });

    return subject?.translatableProperties?.rawProperties
      ? Object.entries(subject.translatableProperties.rawProperties).reduce(
          (desc, [key, value]) =>
            desc.replace(new RegExp(`{{${key}}}`, "g"), value),
          description
        )
      : description;
  };

  const dataFormatted =
    logSystemData?.docs?.map((doc) => {
      return {
        category: doc?.category ?? "",
        subcategory: doc?.subcategory ?? "",
        environment: doc?.environment ?? "",
        subjectKey: t(
          SubjectStatusLogSystemEnum[doc.subject.key] ?? doc?.subject?.key,
          {
            ns: "log-system",
          }
        ),
        subjectDescription: getDescription(doc.subject),
        actor: doc.actor ?? "",
        timestampDate: format(new Date(doc.timestampDate), "dd/MM/yyyy HH:mm:ss"),
      };
    }) || [];

  const columns: ColumnDef<any, any>[] = [
    {
      header: t("react-table-column-event-date"),
      footer: (props: any) => props.column.id,
      accessorKey: "timestampDate",
    },
    {
      header: t("react-table-column-subject"),
      footer: (props: any) => props.column.id,
      accessorKey: "subjectKey",
    },
    {
      header: t("react-table-column-description"),
      footer: (props: any) => props.column.id,
      accessorKey: "subjectDescription",
      cell: ({ row }: any) => {
        return row.original?.subjectDescription == "" ? (
          "---"
        ) : (
          <Tooltip
            placement='bottom-end'
            title={
              <>
                {row.original?.subjectDescription
                  ?.split(";")
                  .map((line: any, index: number) => (
                    <p key={index}>{line}</p>
                  ))}
              </>
            }
          >
            <Box display='flex' flex={"0 1"}>
              <p
                style={{
                  width: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.original?.subjectDescription.replaceAll(";", "; ")}
              </p>

              <Info style={{ marginLeft: 5 }} color='disabled' />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      header: t("react-table-column-actor"),
      footer: (props: any) => props.column.id,
      accessorKey: "actor",
    },
    {
      header: "",
      footer: (props: any) => props.column.id,
      accessorKey: "actions",
      enableColumnFilter: false,
    },
  ].filter((element) => element);

  const renderListLogSystemData = (isLogSystemData: boolean) => {
    return isLogSystemData ? (
      <div style={{ padding: "2rem 1rem" }}>
        <Skeleton />
      </div>
    ) : (
      <div>
        <DialogContent>
          <PaginatedTable
            columns={columns}
            data={dataFormatted || []}
            tableState={tableData}
            pageCount={logSystemData?.totalPages}
            setTableState={setTableData}
            hasFilters={false}
          />
        </DialogContent>
      </div>
    );
  };

  return (
    <>
      <Dialog
        open={modalOpen}
        fullWidth
        maxWidth={"lg"}
        scroll='paper'
        onClose={() => {
          onClose();
        }}
      >
        <DialogTitle>
          {t("log-system", { ns: "modals" })}
          <Button
            justIcon
            simple
            color='tumblr'
            round
            size='sm'
            style={{
              position: "absolute",
              right: "10px",
              top: "10px",
            }}
            onClick={onClose}
          >
            <Close />
          </Button>
        </DialogTitle>
        <Divider style={{ backgroundColor: "#808080" }} />
        {renderListLogSystemData(isLogSystemData)}
      </Dialog>
    </>
  );
};

export default LogSystemModal;
